/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { TokenForm } from './TokenForm';
import { TokenConfig } from '../types/tokens';
import { ArrowLeft, Check, Copy, ExternalLink, Terminal, Shield, AlertTriangle, Coins, Image as ImageIcon, ArrowRight, Server, Globe, ChevronDown, Activity, DollarSign, Store, Lock, TrendingUp, ShieldCheck, ArrowDown, Wallet } from 'lucide-react';
import { calculateTokenScore } from '../lib/utils/rating';
import { useNetwork } from '../hooks/use-network';
import { useWallet } from '../providers/WalletProvider';
import { Network, Chain } from '../types/networks';
import { fetchTokenPrice } from '../data/prices';
import { LogoIcon } from './logo-icon';
import { TokenHeaderPlaceholder } from './token-header-placeholder';

interface LaunchpadProps {
  onBack: () => void;
  onGoToMarket?: (ticker: string) => void;
  onConnectWallet: () => void;
}

export const Launchpad: React.FC<LaunchpadProps> = ({ onBack, onGoToMarket, onConnectWallet }) => {
  const { currentCluster, switchNetwork } = useNetwork();
  const { chain, isConnected } = useWallet();

  const [previewData, setPreviewData] = useState<Partial<TokenConfig>>({
    name: 'Token Name',
    ticker: 'TICKER',
    supply: 1000000,
    chain: chain || 'SOL',
    network: currentCluster.network,
    tokenStandard: 'SPL',
    tags: []
  });

  const [deployedHash, setDeployedHash] = useState<string | null>(null);
  const [deployedTicker, setDeployedTicker] = useState<string>('');
  const [currentGasPrice, setCurrentGasPrice] = useState<string>("0.000005");
  const [solPrice, setSolPrice] = useState<number>(0);

  useEffect(() => {
      setPreviewData(prev => ({ 
          ...prev, 
          chain: chain || 'SOL', 
          network: currentCluster.network 
      }));
  }, [chain, currentCluster]);

  useEffect(() => {
      const getLiveStats = async () => {
          const gas = chain === 'SUI' ? "0.003 SUI" : "0.000005 SOL";
          setCurrentGasPrice(gas);
          
          if (chain) {
              const price = await fetchTokenPrice(chain);
              setSolPrice(price.price);
          }
      };
      getLiveStats();
      const interval = setInterval(getLiveStats, 10000);
      return () => clearInterval(interval);
  }, [chain, currentCluster.network]);

  const handlePreviewUpdate = (data: Partial<TokenConfig>) => {
    setPreviewData(data);
  };

  const handleSuccess = (hash: string, ticker: string) => {
    // Legacy: TokenForm now handles success view internally to show chart.
  };

  const score = calculateTokenScore(previewData);
  const getScoreColor = (s: number) => {
      if (s >= 80) return 'text-green-500';
      if (s >= 50) return 'text-yellow-500';
      return 'text-red-500';
  };

  const isToken2022 = previewData.chain === 'SOL' && previewData.tokenStandard === 'TOKEN_2022';
  const isNft = previewData.tags?.includes('NFT');

  return (
    <div className="min-h-screen bg-[#F9F8F4] flex flex-col">
      <main className="flex-1 container mx-auto px-4 sm:px-6 py-8 md:py-12 grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
        
        <div className="col-span-full mb-4 flex items-center justify-between">
             <div className="flex items-center gap-4">
                <button onClick={onBack} className="p-2 hover:bg-stone-200 rounded-full text-stone-600 transition-colors bg-white shadow-sm border border-stone-200">
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h2 className="font-serif text-2xl font-bold text-stone-900">One-Click Minting</h2>
                    <p className="text-sm text-stone-500">Automated deployment pipeline.</p>
                </div>
             </div>
        </div>

        <div className="order-2 lg:order-1">
            <TokenForm 
                onPreviewUpdate={handlePreviewUpdate} 
                onSuccess={handleSuccess} 
                onConnectWallet={onConnectWallet}
                onGoToMarket={onGoToMarket} 
            />
        </div>

        <div className="order-1 lg:order-2">
            <div className="flex flex-col space-y-6 sticky top-24">
                <div className="flex items-center justify-between px-1">
                    <span className="text-xs font-bold text-stone-400 uppercase tracking-widest flex items-center gap-2">
                        <Activity size={14} /> Live Preview
                    </span>
                    {!isNft && (
                        <span className={`text-xs font-bold font-mono px-2 py-1 rounded-md bg-white border border-stone-200 shadow-soft-md ${getScoreColor(score)}`}>
                            Trust Score: {score}/100
                        </span>
                    )}
                </div>

                <div className="bg-white rounded-2xl border border-stone-200 shadow-soft-2xl overflow-hidden group transition-all duration-500 hover:shadow-soft-2xl hover:border-stone-300 effect-frame flex-1 flex flex-col">
                    <div className="h-52 w-full bg-stone-100 relative overflow-hidden shrink-0">
                        {previewData.headerImageUrl ? (
                            <img src={previewData.headerImageUrl} alt="Header" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        ) : (
                            <TokenHeaderPlaceholder />
                        )}
                        <div className="absolute top-3 left-3 flex flex-wrap gap-2 max-w-[70%]">
                            {isNft && (
                                <span className="px-2 py-1 bg-purple-50/90 backdrop-blur-sm text-purple-800 text-[10px] font-bold uppercase rounded-full shadow-soft-sm border border-purple-100 flex items-center gap-1">
                                    <ImageIcon size={10} /> NFT
                                </span>
                            )}
                            {isToken2022 && (
                                <>
                                    {previewData.extensions?.transferFeeBasisPoints && previewData.extensions.transferFeeBasisPoints > 0 && (
                                        <span className="px-2 py-1 bg-amber-50/90 backdrop-blur-sm text-amber-800 text-[10px] font-bold uppercase rounded-full shadow-soft-sm border border-amber-100 flex items-center gap-1">
                                            <Shield size={10} /> Tax {(previewData.extensions.transferFeeBasisPoints / 100).toFixed(1)}%
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="absolute top-3 right-3">
                            <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-stone-900 text-[10px] font-bold uppercase rounded-full shadow-soft-sm border border-stone-200 flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div> Live
                            </span>
                        </div>
                    </div>
                    <div className="px-6 pb-6 relative flex-1 flex flex-col">
                        <div className="flex justify-between items-end -mt-10 mb-4">
                            <div className="relative w-20 h-20 rounded-md bg-white p-1.5 shadow-soft-xl z-10">
                                <div className="w-full h-full rounded-md bg-stone-900 flex items-center justify-center overflow-hidden text-white font-serif text-2xl border border-stone-100">
                                    {previewData.imageUrl ? (
                                        <img src={previewData.imageUrl} alt="Logo" className="w-full h-full object-cover" />
                                    ) : (
                                        <span>{previewData.name ? previewData.name[0] : '?'}</span>
                                    )}
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm border border-stone-100">
                                    <img 
                                        src={previewData.chain === 'SUI' ? 'https://cryptologos.cc/logos/sui-sui-logo.png' : 'https://cryptologos.cc/logos/solana-sol-logo.png'} 
                                        alt={previewData.chain}
                                        className="w-4 h-4 object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-6">
                            <h3 className="font-serif text-3xl text-stone-900 leading-tight mb-1">{previewData.name || 'Token Name'}</h3>
                            <div className="flex items-center gap-2">
                                {!isNft && <span className="text-sm font-bold text-stone-500 font-mono tracking-tight">${previewData.ticker || 'TICKER'}</span>}
                                {previewData.tokenStandard && !isNft && <span className="text-[10px] font-bold bg-stone-100 px-1.5 py-0.5 rounded text-stone-500 uppercase">{previewData.tokenStandard}</span>}
                                {isNft && <span className="text-[10px] font-bold bg-purple-100 px-1.5 py-0.5 rounded text-purple-700 uppercase">1 of 1</span>}
                            </div>
                             {isToken2022 && (
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    {previewData.extensions?.immutable && (
                                        <span className="flex items-center gap-1 text-[9px] font-bold bg-stone-100 text-stone-600 px-2 py-0.5 rounded border border-stone-200">
                                            <ShieldCheck size={10} /> Immutable
                                        </span>
                                    )}
                                    {previewData.extensions?.revokeMint && (
                                        <span className="flex items-center gap-1 text-[9px] font-bold bg-stone-100 text-stone-600 px-2 py-0.5 rounded border border-stone-200">
                                            <Lock size={10} /> Fixed Supply
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        {!isNft && (
                            <div className="mt-auto">
                                <div className="mb-6 p-4 bg-stone-50 rounded-xl border border-stone-100">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">Instant Swap Preview</span>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="bg-white p-2.5 rounded-lg border border-stone-200 flex justify-between items-center shadow-sm">
                                            <span className="font-mono text-sm font-bold text-stone-900">1.0</span>
                                            <div className="flex items-center gap-1.5 bg-stone-50 px-2 py-1 rounded-md border border-stone-100">
                                                <img src={previewData.chain === 'SUI' ? "https://cryptologos.cc/logos/sui-sui-logo.png" : "https://cryptologos.cc/logos/solana-sol-logo.png"} className="w-3.5 h-3.5" alt={previewData.chain} />
                                                <span className="text-xs font-bold text-stone-600">{previewData.chain}</span>
                                            </div>
                                        </div>
                                        <div className="flex justify-center -my-3 relative z-10"><div className="bg-stone-50 p-1 rounded-full border border-stone-200"><div className="bg-white p-1 rounded-full shadow-sm text-stone-400"><ArrowDown size={12} /></div></div></div>
                                        <div className="bg-white p-2.5 rounded-lg border border-stone-200 flex justify-between items-center shadow-sm">
                                            <span className="font-mono text-sm font-bold text-stone-900">12,500</span>
                                            <div className="flex items-center gap-1.5 bg-stone-50 px-2 py-1 rounded-md border border-stone-100">
                                                {previewData.imageUrl ? <img src={previewData.imageUrl} className="w-3.5 h-3.5 rounded-full object-cover" alt="Token" /> : <div className="w-3.5 h-3.5 rounded-full bg-stone-300"></div>}
                                                <span className="text-xs font-bold text-stone-600 max-w-[60px] truncate">{previewData.ticker}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        <div className="pt-4 border-t border-stone-100 flex justify-between items-center text-[10px] text-stone-400 font-mono mt-auto">
                            <div className="flex items-center gap-1"><Server size={10} /><span>Gas: {currentGasPrice}</span></div>
                            <div className="flex items-center gap-1"><DollarSign size={10} /><span>{chain}: ${solPrice.toFixed(2)}</span></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </main>
    </div>
  );
};